import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getPendingUsers,
  approvePendingUser,
  rejectPendingUser,
  bulkApprovePendingUsers,
  resendActivationEmail,
  type PendingUser
} from '../api/pendingUsers';

/**
 * Hook to fetch pending users
 */
export const usePendingUsers = (page: number = 1, pageSize: number = 25, search: string = '') => {
  return useQuery({
    queryKey: ['pendingUsers', page, pageSize, search],
    queryFn: () => getPendingUsers(page, pageSize, search),
    staleTime: 30000, // 30 seconds
  });
};

/**
 * Hook to approve a pending user
 */
export const useApprovePendingUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => approvePendingUser(userId),
    onSuccess: () => {
      // Invalidate and refetch pending users
      queryClient.invalidateQueries({ queryKey: ['pendingUsers'] });
    },
  });
};

/**
 * Hook to reject a pending user
 */
export const useRejectPendingUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, reason }: { userId: string; reason?: string }) => 
      rejectPendingUser(userId, reason),
    onSuccess: () => {
      // Invalidate and refetch pending users
      queryClient.invalidateQueries({ queryKey: ['pendingUsers'] });
    },
  });
};

/**
 * Hook to bulk approve pending users
 */
export const useBulkApprovePendingUsers = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userIds: string[]) => bulkApprovePendingUsers(userIds),
    onSuccess: () => {
      // Invalidate and refetch pending users
      queryClient.invalidateQueries({ queryKey: ['pendingUsers'] });
    },
  });
};

/**
 * Hook to resend activation email
 */
export const useResendActivationEmail = () => {
  return useMutation({
    mutationFn: (userId: string) => resendActivationEmail(userId),
  });
};
